import { FC, PropsWithChildren, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  List,
  listClasses,
  Paper,
  paperClasses,
  PaperProps,
  styled,
  Typography,
  TypographyProps,
} from '@mui/material';
import classNames from 'classnames';

import useTitleContext from '@hooks/useTitleContext';
import { TitleType } from '@models/Title';

type SectionProps = PaperProps & {
  category?: 'cast' | 'playbackOptions' | 'scoreRating';
  children: ReactNode;
  disableMargin?: boolean;
  empty?: boolean;
  heading?: ReactNode;
  titleType?: TitleType;
};

export const SectionHeading: FC<PropsWithChildren<TypographyProps>> = ({ children, ...props }) => {
  const { playbackOptionId } = useTitleContext();

  return (
    <Typography
      color={playbackOptionId ? 'info.main' : 'text.primary'}
      component='h3'
      gutterBottom
      variant='h5'
      {...props}
    >
      {children}
    </Typography>
  );
};

const SectionWrapper = styled(Box)`
  display: flex;
  flex-direction: column;

  &:not(.disableMargin) {
    margin-top: ${({ theme }) => theme.spacing(2)};
  }

  &.${paperClasses.root} {
    overflow: hidden;
  }

  &.${listClasses.root} {
    padding: 0;
  }
`;

const Section: FC<SectionProps> = ({
  category,
  children,
  disableMargin,
  empty,
  heading,
  titleType,
  ...rest
}) => {
  const { t } = useTranslation();
  const labelId = `${category}-section-label`;

  return (
    <SectionWrapper className={classNames({ disableMargin })}>
      {category && (
        <SectionHeading id={labelId}>
          {heading ?? t(`titleDetails.${category}`, { context: titleType })}
        </SectionHeading>
      )}

      <Paper {...rest}>
        {empty ? (
          <Box alignItems='center' display='flex' justifyContent='center' p={2}>
            <Typography>{t(`titleDetails.${category}`, { context: 'missing' })}</Typography>
          </Box>
        ) : (
          <List aria-labelledby={labelId} dense>
            {children}
          </List>
        )}
      </Paper>
    </SectionWrapper>
  );
};

export default Section;
